import type { HTMLAttributes, ReactNode } from 'react';
import { IcnSpinner } from '../../icons/index.js';
import { useI18nT } from '../../i18n/index.js';

export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'className'> {
  size?: SpinnerSize;
  label?: string;
  className?: string;
}

const SIZE_PX: Record<SpinnerSize, number> = { sm: 14, md: 20, lg: 28 };

/** Indeterminate activity indicator — wraps the animated spinner icon
 *  and adds a polite live-region label for assistive tech. */
export function Spinner(props: SpinnerProps): ReactNode {
  const { size = 'md', label, className, ...rest } = props;
  const t = useI18nT();
  const classes = ['vq-spinner', className].filter(Boolean).join(' ');
  const announced = label ?? t('spinner.defaultLabel');
  return (
    <span
      {...rest}
      className={classes}
      data-component="Spinner"
      data-size={size}
      role="status"
      aria-live="polite"
      aria-label={label !== undefined ? undefined : announced}
    >
      <IcnSpinner size={SIZE_PX[size]} title={announced} />
      {label !== undefined ? <span className="vq-spinner__label">{label}</span> : null}
    </span>
  );
}
